import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import { CssBaseline, GlobalStyles } from "@mui/material";
import viewsStyles from "../assets/Styles/view.styles.bridge";
import { getFromLocalStorage } from "../utils/localStorage/localStorage.utils";
import { iUserPermissions } from "../types/permissions";
import { configureChartTheme } from '../utils/chartConfig';

type ThemeContextType = {
  darkMode: boolean;
  toggleDarkMode: () => void;
  // eslint-disable-next-line no-unused-vars
  setDarkMode: (value: boolean) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const getThemeModeFromUser = (): boolean => {
  try {
    const user = getFromLocalStorage<iUserPermissions>("user");
    if (user?.preferences?.theme) {
      const themeMode = user.preferences.theme;
      if (themeMode === "dark") {
        return true;
      } else if (themeMode === "system") {
        return (
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
        );
      } else {
        return false; // light mode or default
      }
    }
  } catch (error) {
    console.error("Error reading theme from user preferences:", error);
  }
  // Fallback: no user found, return false (light mode)
  return false;
};

const ThemeProviderContent: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const isLoginRoute = location.pathname === "/login";
  
  const [darkMode, setDarkModeState] = useState<boolean>(() => {
    // On login page, always use light mode
    if (isLoginRoute) {
      return false;
    }
    // Otherwise, get user preferences (or default to light if no user)
    return getThemeModeFromUser();
  });

  // Listen for route changes and user data updates
  useEffect(() => {
    // On login page, force light mode
    if (isLoginRoute) {
      setDarkModeState(false);
      document.body.classList.remove("dark-mode");
      return;
    }

    // Off login page, apply user preferences
    const currentTheme = getThemeModeFromUser();
    setDarkModeState(currentTheme);
    
    // Apply immediately when leaving login page
    if (currentTheme) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }
    configureChartTheme(currentTheme);

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "user") {
        const newThemeMode = getThemeModeFromUser();
        setDarkModeState(newThemeMode);
      }
    };

    // Listen for storage changes from other tabs
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isLoginRoute]);

  // Global listener for theme updates (independent of route)
  useEffect(() => {
    const handleGlobalThemeUpdate = () => {
      // Always update theme when user data changes, even on login page
      const newThemeMode = getThemeModeFromUser();
      setDarkModeState(newThemeMode);
      
      // Apply immediately
      if (newThemeMode) {
        document.body.classList.add("dark-mode");
      } else {
        document.body.classList.remove("dark-mode");
      }
      configureChartTheme(newThemeMode);
    };

    window.addEventListener("localStorageUserUpdated", handleGlobalThemeUpdate);
    
    return () => {
      window.removeEventListener("localStorageUserUpdated", handleGlobalThemeUpdate);
    };
  }, []);

  // Sync theme to body and localStorage
  useEffect(() => {
    // setInLocalStorage('userDarkMode', darkMode.toString());
    
    // Configurar tema global de Chart.js
    configureChartTheme(darkMode);
    
    // Agregar o quitar clase dark-mode del body
    if (darkMode) {
      document.body.classList.add("dark-mode");
    } else {
      document.body.classList.remove("dark-mode");
    }

    // Only save to localStorage if not on login page
    if (!isLoginRoute) {
      // setInLocalStorage("userDarkMode", darkMode.toString());
    }

    // Cleanup: cuando el componente se desmonta, remover la clase dark-mode
    return () => {
      document.body.classList.remove("dark-mode");
    };
  }, [darkMode, isLoginRoute]);

  const toggleDarkMode = () => {
    setDarkModeState((prev) => !prev);
  };

  const setDarkMode = (value: boolean) => {
    setDarkModeState(value);
  };

  const theme = useMemo(() => {
    const baseTheme = createTheme({
      palette: {
        mode: darkMode ? "dark" : "light",
        ...(darkMode
          ? {
              // Colores para modo oscuro - NEGRO
              background: {
                default: "#000000",
                paper: "#1a1a1a",
              },
              primary: {
                main: "#0FB0C2",
                light: "#2FC8B8",
                dark: "#0a8a99",
              },
              secondary: {
                main: "#2FC8B8",
                light: "#4dd9c9",
                dark: "#1fa89a",
              },
              text: {
                primary: "#ffffff",
                secondary: "#b8b9c9",
              },
              divider: "rgba(255, 255, 255, 0.12)",
              action: {
                active: "#ffffff",
                hover: "rgba(15, 176, 194, 0.08)",
                selected: "rgba(15, 176, 194, 0.16)",
                disabled: "rgba(255, 255, 255, 0.3)",
                disabledBackground: "rgba(255, 255, 255, 0.12)",
              },
              error: {
                main: "#FF4262",
              },
              success: {
                main: "#0FB0C2",
              },
            }
          : {
              // Colores para modo claro (mantener los actuales)
              background: {
                default: "#f5f5f5",
                paper: "#ffffff",
              },
              primary: {
                main: "#0FB0C2",
              },
              secondary: {
                main: "#2FC8B8",
              },
            }),
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: darkMode
              ? {
                  backgroundImage: "none",
                  backgroundColor: "#1a1a1a",
                }
              : {
                  backgroundImage: "none",
                },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: darkMode
              ? {
                  backgroundColor: "#1a1a1a",
                  color: "#ffffff",
                }
              : {},
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: darkMode
              ? {
                  backgroundColor: "#1a1a1a",
                  color: "#ffffff",
                }
              : {},
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: darkMode
              ? {
                  borderColor: "rgba(255, 255, 255, 0.12)",
                  color: "#ffffff",
                }
              : {},
            head: darkMode
              ? {
                  backgroundColor: "#2a2a2a",
                  color: "#ffffff",
                }
              : {},
            body: darkMode
              ? {
                  backgroundColor: "#1a1a1a",
                }
              : {},
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: darkMode
              ? {
                  "&:nth-of-type(even)": {
                    backgroundColor: "#151515",
                  },
                  "&:hover": {
                    backgroundColor: "rgba(15, 176, 194, 0.08)",
                  },
                }
              : {},
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#ffffff",
                  "& .MuiInputBase-input": {
                    color: "#ffffff",
                  },
                }
              : {},
            input: darkMode
              ? {
                  color: "#ffffff",
                  "&::placeholder": {
                    color: "#bdbdbd",
                    opacity: 1,
                  },
                }
              : {},
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#b8b9c9",
                  "&.Mui-focused": {
                    color: "#0FB0C2",
                  },
                }
              : {},
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: darkMode
              ? {
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#2a2a2a",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.23)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#0FB0C2",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#b8b9c9",
                  },
                  "& .MuiInputBase-input": {
                    color: "#ffffff",
                  },
                }
              : {},
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: darkMode
              ? {
                  backgroundColor: "#2a2a2a",
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                  },
                  "& .MuiInputBase-input": {
                    color: "#ffffff",
                  },
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.4)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0FB0C2",
                  },
                }
              : {},
            input: darkMode
              ? {
                  color: "#ffffff",
                }
              : {},
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: darkMode
              ? {
                  backgroundColor: "#2a2a2a",
                  color: "#ffffff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.4)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#0FB0C2",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                  },
                }
              : {},
          },
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#bdbdbd",
                }
              : {},
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: darkMode
              ? {
                  backgroundColor: "#2a2a2a",
                }
              : {},
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#0fb0c2",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(15, 176, 194, 0.16)",
                    "&:hover": {
                      backgroundColor: "#0fb0c2",
                    },
                  },
                }
              : {},
          },
        },
        MuiButton: {
          styleOverrides: {
            root: darkMode
              ? {
                  "&.MuiButton-contained": {
                    backgroundColor: "#0FB0C2",
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#1a1a1a",
                    },
                  },
                }
              : {},
          },
        },
        MuiCard: {
          styleOverrides: {
            root: darkMode
              ? {
                  backgroundColor: "#1a1a1a",
                  backgroundImage: "none",
                }
              : {},
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "rgba(255, 255, 255, 0.7)",
                  "&.Mui-checked": {
                    color: "#0FB0C2",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 0.8,
                  },
                  "&.Mui-disabled .MuiSvgIcon-root": {
                    color: "rgba(255, 255, 255, 0.5)",
                  },
                  "&.Mui-disabled .MuiTouchRipple-root": {
                    display: "none",
                  },
                }
              : {},
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: darkMode
              ? {
                  backgroundColor: "#1a1a1a",
                }
              : {},
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#ffffff",
                }
              : {},
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#ffffff",
                }
              : {},
          },
        },
        MuiList: {
          styleOverrides: {
            root: darkMode
              ? {
                  backgroundColor: "#1a1a1a",
                }
              : {},
          },
        },
        MuiListItem: {
          styleOverrides: {
            root: darkMode
              ? {
                  color: "#ffffff",
                  "&:hover": {
                    backgroundColor: "rgba(15, 176, 194, 0.08)",
                  },
                }
              : {},
          },
        },
      },
    });

    // Agregar customStyles al tema
    (baseTheme as any).customStyles = viewsStyles;

    return baseTheme;
  }, [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, setDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {darkMode && (
          <GlobalStyles
            styles={{
              "& .MuiMenuItem-root:hover": {
                backgroundColor: "#0fb0c2 !important",
              },
              "& .MuiMenu-paper .MuiMenuItem-root:hover": {
                backgroundColor: "#0fb0c2 !important",
              },
            }}
          />
        )}
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <ThemeProviderContent>{children}</ThemeProviderContent>;
};
